import useSlug from '../../utils/use-slug';
import styles from '../../styles/Metrics.module.scss';
import { useMetrics } from './use-metrics';
import { ExternalTripCountsData, Settings, VehicleClass } from '../../data/api';
import MetricsCharts from './MetricsCharts';
import { unitsShorthand } from '../region/MetricTable';
import { useLinearUnitsInfo } from '../../utils/use-linear-units';
import { convertLinearUnitsForDisplay } from '../../utils/units-conversion';
import MetricItem from './MetricItem';
import { getExternalCountForPeriod } from '../../utils/external-trip-counts';

const m = {
    population: {
        name: 'Population',
        tooltip:
            'Population data is from Census Reporter, Australian Bureau of Statistics and NZ Stats.',
    },
    avgVehicles: {
        name: 'Average Vehicles',
        tooltip:
            'Average number of active shared vehicles for the time period selected. Active vehicles refer to vehicles that are available for rent or rented.',
    },
    avgTripSpeed: {
        name: 'Average Trip Speed',
        tooltip: 'Average speed of shared vehicle trips.',
    },
    totalDistance: {
        name: 'Total Distance',
        tooltip:
            'Total distance ridden by shared vehicles for the time period selected.',
    },
    totalTrips: {
        name: 'Total Trips',
        tooltip: 'Total shared vehicle trips for the time period selected.',
    },
};

interface MetricsProps {
    timeRange: string;
    vehicleClass: VehicleClass;
    citySettings: Settings;
    externalTripCounts?: ExternalTripCountsData | null;
}

function Metrics({
    timeRange,
    vehicleClass,
    citySettings,
    externalTripCounts,
}: MetricsProps) {
    const linearUnits = useLinearUnitsInfo();
    const slug = useSlug();
    const metrics = useMetrics(slug, timeRange, vehicleClass);

    if (!metrics) {
        return (
            <div className={styles.metricsRow}>
                <MetricItem
                    name={m.population.name}
                    value={undefined}
                    unit={undefined}
                    tooltipText={m.population.tooltip}
                />
                <MetricItem
                    name={m.avgVehicles.name}
                    value={undefined}
                    unit={undefined}
                    tooltipText={m.avgVehicles.tooltip}
                />
                <MetricItem
                    name={m.totalTrips.name}
                    value={undefined}
                    unit={undefined}
                    tooltipText={m.totalTrips.tooltip}
                    timeRange={timeRange}
                    vehicleClass={vehicleClass}
                    externalTripCounts={externalTripCounts}
                    citySettings={citySettings}
                    dataBuckets={null}
                />
                <MetricItem
                    name={m.totalDistance.name}
                    value={undefined}
                    unit={undefined}
                    tooltipText={m.totalDistance.tooltip}
                />
                <MetricItem
                    name={m.avgTripSpeed.name}
                    value={undefined}
                    unit={undefined}
                    tooltipText={m.avgTripSpeed.tooltip}
                />
            </div>
        );
    }

    return (
        <>
            <div className={styles.metricsRow}>
                <MetricItem
                    name={m.population.name}
                    value={citySettings.population}
                    unit={undefined}
                    tooltipText={m.population.tooltip}
                />
                <MetricItem
                    name={m.avgVehicles.name}
                    value={metrics.meanActiveVehiclesPerDay.average}
                    unit={metrics.meanActiveVehiclesPerDay.yUnit}
                    tooltipText={m.avgVehicles.tooltip}
                />
                <MetricItem
                    name={m.totalTrips.name}
                    value={metrics.totalTripCount}
                    unit="trips"
                    tooltipText={m.totalTrips.tooltip}
                    timeRange={timeRange}
                    vehicleClass={vehicleClass}
                    externalTripCounts={externalTripCounts}
                    citySettings={citySettings}
                    dataBuckets={metrics.tripsPerDay.dataBuckets}
                />
                <MetricItem
                    name={m.totalDistance.name}
                    value={
                        metrics.tripDistance.sumTotal
                            ? convertLinearUnitsForDisplay(
                                  metrics.tripDistance.sumTotal,
                                  metrics.tripDistance.xUnit,
                                  linearUnits
                              )
                            : undefined
                    }
                    unit={unitsShorthand[linearUnits.units]}
                    tooltipText={m.totalDistance.tooltip}
                />
                <MetricItem
                    name={m.avgTripSpeed.name}
                    value={
                        metrics.tripDistance.average
                            ? convertLinearUnitsForDisplay(
                                  metrics.tripDistance.average,
                                  metrics.tripDistance.xUnit,
                                  linearUnits
                              ) /
                              (metrics.tripDuration.average / 60)
                            : undefined
                    }
                    unit={unitsShorthand[linearUnits.units] + '/hr'}
                    maximumFractionDigits={2}
                    tooltipText={m.avgTripSpeed.tooltip}
                />
            </div>
            <MetricsCharts
                citySettings={citySettings}
                metrics={metrics}
                timeRange={timeRange}
                vehicleClass={vehicleClass}
                externalTripCounts={externalTripCounts}
            />
        </>
    );
}

export default Metrics;
